'use client';

import { useEffect, useRef, useState } from 'react';
import { todayInArgentina, useNextAvailable } from '@contexts/next-available';
import { trackEvent } from '../../../lib/analytics/posthog';
import { EVENTS } from '../../../lib/analytics/events';
import { formatPrice } from '@utils/price';
import { getSeoPrice } from '@utils/seo-locale';
import { whatsappUrl } from '@utils/phone';
import { vendorStorefrontUrl } from '@utils/storefront-url';

const WHATSAPP_ICON =
  'M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2Zm5.4 14.2c-.2.6-1.3 1.2-1.8 1.3-.5.1-1 .1-1.7-.1-.4-.1-.9-.3-1.5-.6-2.6-1.1-4.3-3.8-4.4-4-.1-.2-1-1.4-1-2.6s.6-1.9.9-2.1c.2-.3.5-.3.7-.3h.5c.2 0 .4 0 .6.5l.8 1.9c.1.2.1.4 0 .5l-.3.5-.4.4c-.1.1-.3.3-.1.6.2.3.7 1.2 1.6 1.9 1.1 1 2 1.3 2.3 1.4.3.1.5.1.6-.1l.9-1c.2-.3.4-.2.6-.1l1.8.9c.3.1.5.2.5.3.1.1.1.7-.1 1.3Z';

/** «Desde $X»: el servicio activo más barato con precio visible. */
function lowestPrice(vendor) {
  let best = null;
  for (const service of vendor.services || []) {
    if (service.status && String(service.status).toLowerCase() !== 'active') continue;
    if (service.is_price_hidden) continue;
    const { price, currency } = getSeoPrice(service, vendor.country);
    if (Number(price) > 0 && (!best || Number(price) < best.price)) best = { price: Number(price), currency };
  }
  return best;
}

const formatShortDate = (date) =>
  new Date(`${date}T12:00:00`).toLocaleDateString('es-AR', { weekday: 'short', day: 'numeric', month: 'short' });

/** «Hay turnos hoy» o «Próximo turno: jue 15 oct». Mientras carga, ocupa su lugar. */
export function Availability({ username, className = '' }) {
  const date = useNextAvailable(username);
  if (date === undefined) return <span className={`book__avail is-loading ${className}`} aria-hidden="true" />;
  if (!date) return null;
  const today = date <= todayInArgentina();
  return (
    <span className={`book__avail ${today ? 'is-today' : ''} ${className}`}>
      {today ? 'Hay turnos hoy' : `Próximo turno: ${formatShortDate(date)}`}
    </span>
  );
}

/**
 * Reservar: tarjeta sobre la portada en escritorio y barra fija abajo en el teléfono. En escritorio,
 * cuando la tarjeta sale de la pantalla aparece la barra (flotando abajo) para no perder el botón.
 * Lleva a elegir día y horario en la página del negocio en agora.red (con atribución), o a
 * escribirle por WhatsApp.
 */
export default function BookingCard({ vendor, campaign = 'vendor_profile' }) {
  const vendorCategory = vendor.services?.[0]?.category_sub_category?.category?.name;
  const bookingHref = vendor.is_created_by_marketplace
    ? null
    : vendorStorefrontUrl(vendor.username, { campaign, content: 'booking_cta' });
  const waHref = whatsappUrl(vendor.user?.phone, { assumeMobile: true, country: vendor.country });
  const lowest = lowestPrice(vendor);
  const cardRef = useRef(null);
  const [docked, setDocked] = useState(false);

  useEffect(() => {
    const card = cardRef.current;
    if (!card || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      // Solo al pasarla de largo hacia abajo (no antes de llegar).
      setDocked(!entry.isIntersecting && entry.boundingClientRect.bottom < 0);
    });
    observer.observe(card);
    return () => observer.disconnect();
  }, []);

  const trackBooking = () =>
    trackEvent(EVENTS.VENDOR_CONTACT_CLICKED, {
      vendor_username: vendor.username,
      contact_type: 'booking_cta',
      destination: 'agora_storefront',
      vendor_category: vendorCategory
    });
  const trackWhatsApp = () =>
    trackEvent(EVENTS.VENDOR_CONTACT_CLICKED, {
      vendor_username: vendor.username,
      contact_type: 'whatsapp',
      destination: 'whatsapp',
      vendor_category: vendorCategory
    });

  if (!bookingHref && !waHref) return null;

  const whatsapp = waHref && (
    <a
      href={waHref}
      target="_blank"
      rel="noopener noreferrer"
      onClick={trackWhatsApp}
      className="book__wa"
      aria-label="Escribir por WhatsApp"
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="#16a34a" aria-hidden="true">
        <path d={WHATSAPP_ICON} />
      </svg>
      <span className="book__wa-text">Escribir por WhatsApp</span>
    </a>
  );

  return (
    <>
      <aside ref={cardRef} className="book" aria-label="Reservar">
        {!vendor.is_created_by_marketplace && <Availability username={vendor.username} />}
        {lowest && (
          <p className="book__from">
            desde <span className="book__price font-display">{formatPrice(lowest.price, lowest.currency)}</span>
          </p>
        )}
        {bookingHref && (
          <a href={bookingHref} onClick={trackBooking} className="book__cta">
            Ver turnos disponibles
          </a>
        )}
        {whatsapp}
        {bookingHref && <p className="book__note">Elegís día y horario en su página de agora.red.</p>}
      </aside>

      <div className={`book-bar ${docked ? 'is-docked' : ''}`}>
        <div className="book-bar__price">
          {lowest && (
            <>
              <span>desde</span>
              <b className="font-display">{formatPrice(lowest.price, lowest.currency)}</b>
            </>
          )}
        </div>
        {waHref && (
          <a
            href={waHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={trackWhatsApp}
            className="book-bar__wa"
            aria-label="Escribir por WhatsApp"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="#16a34a" aria-hidden="true">
              <path d={WHATSAPP_ICON} />
            </svg>
          </a>
        )}
        {bookingHref && (
          <a href={bookingHref} onClick={trackBooking} className="book-bar__cta" aria-label="Ver turnos disponibles">
            Ver turnos
          </a>
        )}
      </div>
    </>
  );
}
